import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Sparkles, Upload, Heart, RefreshCw, Copy, Wand2 } from "lucide-react";
import { aiAnalyzeLogo, aiRemoveBg, aiGenerate, aiGetSession, aiSelectConcept, uploadArtwork, trackEvent, aiGetJob, aiPollJob } from "../lib/api";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DIRECTIONS = [
  ["clean_modern_performance", "Clean Professional", "Simple, premium brand-led design: strong branding, clean border, crisp markings, minimal supporting graphics."],
  ["futuristic_precision", "Futuristic Precision", "Sophisticated angular / tech styling, never gaming graphics."],
  ["sprint_performance", "Sprint Performance", "Functional high-end sprint track with movement and branding."],
  ["premium_minimal", "Premium Minimal", "Large branding, negative space, minimal supporting geometry."],
  ["bold_geometric", "Bold Geometric", "Expressive circles / hexagons / rings, professionally composed."],
  ["ai_choice", "AI Designer's Choice", "AI picks the family that best suits your branding."],
];
const MOTIFS = ["Let AI Choose", "Chevrons", "Arrows", "Hexagons", "Circles", "Rings", "Diamonds", "Triangles", "Blocks", "Angular"];
const MOTIF_KEY = { "Let AI Choose": "auto", Chevrons: "chevron", Arrows: "arrow", Hexagons: "hexagon", Circles: "circle", Rings: "ring", Diamonds: "diamond", Triangles: "triangle", Blocks: "block", Angular: "angular" };
const INTENSITIES = [["clean", "Clean"], ["balanced", "Balanced"], ["bold", "Bold"]];
const MARK_STYLES = [
  ["none", "No Markings", "Clean designed turf, no metre markings."],
  ["metre_markings", "Metre Markings Only", "Professional metre line markers at each position."],
  ["numbers", "Numbers Only", "Metre numbers at each interval, proportionally placed."],
  ["markings_numbers", "Markings + Numbers", "Metre lines together with their numbers."],
  ["boxed_numbers", "Boxed Numbers", "Each metre position a clean numbered zone / box."],
  ["sprint_track", "Sprint Track", "Sprint-track styling with start/finish + performance markings."],
  ["lane_training", "Lane / Training Track", "Lane-based training design (great on 2m width)."],
  ["ai_choice", "AI Designer's Choice", "AI picks the marking treatment for your style."],
];
const MARK_POS = [["centre", "Centre"], ["both_sides", "Both Sides"], ["lane_based", "Lane Based"]];
// Styles that combine metre lines + numbers — not offered on 2-lane tracks (too cluttered on narrow lanes).
const COMBINED_LINE_NUMBER = ["markings_numbers", "boxed_numbers", "sprint_track", "lane_training"];
const STAGES = ["Analysing your branding…", "Matching your colours…", "Building your layout…", "Creating your concepts…"];
const LIMIT_MSG = "You've explored all AI concepts for this design. Choose a favourite to continue customising.";
const SESSION_KEY = "pgt_ai_session";
const JOB_KEY = "pgt_ai_job";
const LONG_MSG = "Your designs are taking a little longer than usual — we're still creating them.";
const uuid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : "r_" + Math.random().toString(36).slice(2) + Date.now());

const Btn = ({ on, children, ...p }) => (
  <button {...p} className={`px-3 py-2 rounded-sm text-sm font-display font-bold uppercase tracking-wide border transition-colors disabled:opacity-40 ${on ? "bg-pgt-green text-black border-pgt-green" : "border-white/15 text-white/80 hover:border-pgt-green"}`}>{children}</button>
);

export default function AIWizard({ tenant, onClose, onUse }) {
  const lim = tenant?.size_limits || { min_length: 1, max_length: 20, min_width: 1, max_width: 5 };
  const turfCols = (tenant?.turf_colours || []).filter((c) => c.available);
  const [step, setStep] = useState(1);
  const [dims, setDims] = useState({ length: 10, width: 1.5 });
  const [lanes, setLanes] = useState(1);
  const layout = lanes >= 2 ? "two_lane" : "single";
  const [logo, setLogo] = useState(null);
  const [logoB64, setLogoB64] = useState(null);
  const [origB64, setOrigB64] = useState(null);
  const [cleanB64, setCleanB64] = useState(null);
  const [logoStage, setLogoStage] = useState("upload"); // upload | confirm
  const [brand, setBrand] = useState([]);
  const [brandSel, setBrandSel] = useState([]); // confirmed brand-colour hexes (ordered: primary, secondary, accent)
  const [viewIdx, setViewIdx] = useState(0);
  const [direction, setDirection] = useState("clean_modern_performance");
  const [motif, setMotif] = useState("Let AI Choose");
  const [intensity, setIntensity] = useState("balanced");
  const [markings, setMarkings] = useState({ style: "markings_numbers", interval: 1, position: "centre", lane_mode: "shared", start_line: false, finish_line: false, sf_text: false });
  const mk = (patch) => setMarkings((p) => ({ ...p, ...patch }));
  const [instructions, setInstructions] = useState("");
  const [noLogo, setNoLogo] = useState(false);
  const [brandingMode, setBrandingMode] = useState("none"); // none | text | ai_logo
  const [bizName, setBizName] = useState("");
  const [slogan, setSlogan] = useState("");
  const [extraWording, setExtraWording] = useState("");
  const [hexInput, setHexInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(0);
  const [concepts, setConcepts] = useState(null);
  const [saved, setSaved] = useState({}); // conceptId -> true
  const [remaining, setRemaining] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [takingLong, setTakingLong] = useState(false);
  const sessionRef = useRef(null);
  const jobRef = useRef(null);
  const lastGenRef = useRef({ mode: "initial", extra: {} });

  if (!sessionRef.current) {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) { sid = uuid(); localStorage.setItem(SESSION_KEY, sid); }
    sessionRef.current = sid;
  }

  useEffect(() => {
    aiGetSession(sessionRef.current)
      .then((s) => { setRemaining(s.remaining); if (s.limit_reached) setLimitReached(true); })
      .catch(() => {});
  }, []);

  // Reconnect to an in-flight / completed initial-generation job after a refresh or return visit.
  useEffect(() => {
    const raw = localStorage.getItem(JOB_KEY);
    if (!raw) return;
    let saved; try { saved = JSON.parse(raw); } catch { localStorage.removeItem(JOB_KEY); return; }
    if (!saved?.job_id) return;
    const restore = () => {
      if (saved.dims) setDims(saved.dims);
      if (saved.lanes) setLanes(saved.lanes); else if (saved.layout) setLanes(saved.layout === "two_lane" ? 2 : 1);
      if (saved.motif) setMotif(saved.motif);
      if (saved.intensity) setIntensity(saved.intensity);
      if (saved.direction) setDirection(saved.direction);
      if (saved.markings) setMarkings(saved.markings);
    };
    (async () => {
      try {
        const job = await aiGetJob(saved.job_id);
        if (job.status === "completed" && job.result?.concepts?.length) {
          restore();
          setConcepts(job.result.concepts);
          if (typeof job.result.remaining === "number") setRemaining(job.result.remaining);
          if (job.result.limit_reached) setLimitReached(true);
          setStep(11);
        } else if (job.status === "queued" || job.status === "generating") {
          restore();
          jobRef.current = saved.job_id; lastGenRef.current = { mode: "initial", extra: {} };
          setBusy(true); setStep(10); setStage(0);
          const timer = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 1400);
          try { const res = await pollJob(saved.job_id); handleGenResult("initial", {}, res, timer); }
          catch (e) { clearInterval(timer); if (e.code === "timeout") { setTakingLong(true); setBusy(false); } else { localStorage.removeItem(JOB_KEY); setBusy(false); } }
        } else {
          localStorage.removeItem(JOB_KEY);
        }
      } catch { localStorage.removeItem(JOB_KEY); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const imgUrl = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);
  const pollJob = (jobId) => aiPollJob(jobId, { timeout: 120000, interval: 2500 });

  const params = () => ({
    session_id: sessionRef.current, request_id: uuid(),
    dimensions: dims, layout, lanes, brand_colours: (brandSel.length ? brandSel : brand.map((b) => b.hex)),
    direction, motif: MOTIF_KEY[motif], intensity, markings, instructions, logo, logo_base64: logoB64, mock: false,
    no_logo: noLogo, branding_mode: noLogo ? brandingMode : "", business_name: bizName, slogan, extra_wording: extraWording,
  });
  const quoteUrl = tenant?.contact?.quote_url || "https://primegymturf.com/#cta";
  const overLimit = dims.length > lim.max_length + 1e-6 || dims.width > lim.max_width + 1e-6;
  const excludeRecipes = () => (concepts || []).map((c) => c.recipe);

  const onLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const res = await uploadArtwork(f);
      const aspect = res.quality?.width && res.quality?.height ? res.quality.width / res.quality.height : 1;
      const url = res.url ? `${process.env.REACT_APP_BACKEND_URL}${res.url}` : undefined;
      setLogo({ fileId: res.file_id, url, aspect });
      trackEvent("ai_logo_uploaded");
      const reader = new FileReader();
      reader.onload = async () => {
        const orig = reader.result;
        setOrigB64(orig);
        try { const a = await aiAnalyzeLogo(orig); setBrand(a.colours || []); setBrandSel((a.colours || []).slice(0, 3).map((c) => c.hex)); trackEvent("ai_brand_analysis_completed", undefined, { count: (a.colours || []).length }); } catch (e2) {}
        let cleaned = null;
        try { const bg = await aiRemoveBg(orig); if (bg.changed && bg.cleaned_base64) cleaned = bg.cleaned_base64; } catch (e3) {}
        setBusy(false);
        if (cleaned) { setCleanB64(cleaned); setLogoStage("confirm"); trackEvent("ai_logo_bg_detected"); }
        else { setLogoB64(orig); setStep(4); }
      };
      reader.readAsDataURL(f);
    } catch (e1) { toast.error("Upload failed"); setBusy(false); }
  };
  const confirmLogo = (useCleaned) => {
    setLogoB64(useCleaned ? cleanB64 : origB64);
    trackEvent(useCleaned ? "ai_logo_bg_removed" : "ai_logo_bg_kept");
    setLogoStage("upload"); setStep(4);
  };
  const toggleBrand = (hex) => setBrandSel((prev) => prev.includes(hex) ? prev.filter((h) => h !== hex) : (prev.length >= 3 ? prev : [...prev, hex]));
  const addHex = () => {
    const h = hexInput.trim();
    if (!/^#?[0-9a-fA-F]{6}$/.test(h)) { toast.error("Enter a valid hex like #1A2B3C"); return; }
    const hx = h.startsWith("#") ? h.toLowerCase() : "#" + h.toLowerCase();
    if (brandSel.includes(hx)) { setHexInput(""); return; }
    if (brandSel.length >= 3) { toast.message("Up to 3 colours."); return; }
    setBrandSel((p) => [...p, hx]); setHexInput("");
  };

  // unified generation entrypoint. mode: initial | more | more_like_this | regenerate_one
  const callGen = async (mode, extra = {}) => {
    if (busy) return; // block double-clicks / concurrent requests
    if (overLimit) { setStep(1); return; } // never spend image credits over the online limit
    setBusy(true); setTakingLong(false);
    lastGenRef.current = { mode, extra };
    if (mode === "initial") { setStep(10); setStage(0); }
    const evt = { initial: "ai_generation_started", more: "ai_generate_more", more_like_this: "ai_more_like_this", regenerate_one: "ai_regenerate_single" }[mode];
    trackEvent(evt, undefined, { direction, motif, intensity });
    let timer;
    if (mode === "initial") timer = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 1400);
    try {
      const sub = await aiGenerate({ ...params(), mode, exclude_recipes: excludeRecipes(), ...extra });
      if (sub.custom_quote) {
        toast.message(sub.message || "This size needs a custom quote.");
        if (mode === "initial") setStep(1);
        if (timer) clearInterval(timer);
        setBusy(false);
        return;
      }
      jobRef.current = sub.job_id;
      if (mode === "initial") localStorage.setItem(JOB_KEY, JSON.stringify({ job_id: sub.job_id, dims, layout, lanes, motif, intensity, direction, markings }));
      const res = await pollJob(sub.job_id);
      handleGenResult(mode, extra, res, timer);
    } catch (e) {
      if (timer) clearInterval(timer);
      if (e.code === "timeout") { setTakingLong(true); setBusy(false); return; } // job continues; show gentle wait UI
      trackEvent("ai_generation_failed", undefined, { mode });
      toast.error("We couldn't create that concept. Try again.");
      if (mode === "initial") setStep(9);
      setBusy(false);
    }
  };

  const handleGenResult = (mode, extra, res, timer) => {
    if (timer) clearInterval(timer);
    setTakingLong(false);
    if (mode === "initial") localStorage.removeItem(JOB_KEY);
    if (res.quality_fallback) toast.message("Generated at standard quality — high quality was briefly unavailable.");
    if (typeof res.remaining === "number") setRemaining(res.remaining);
    if (res.limit_reached && (!res.concepts || !res.concepts.length)) {
      setLimitReached(true);
      toast.message(res.message || LIMIT_MSG);
      if (mode === "initial") setStep(9);
    } else {
      if (mode === "initial") { setConcepts(res.concepts); setViewIdx(0); setStep(11); trackEvent("ai_concept_viewed", undefined, { count: res.concepts.length }); }
      else if (mode === "regenerate_one") setConcepts((prev) => prev.map((c) => (c.id === extra._slotId ? (res.concepts[0] || c) : c)));
      else setConcepts((prev) => [...(prev || []), ...res.concepts]);
      trackEvent("ai_generation_completed", undefined, { mode, produced: res.concepts.length });
      if (res.limit_reached) setLimitReached(true);
    }
    setBusy(false);
  };

  // "Keep Waiting" — reconnect to the SAME job (never starts a new paid generation).
  const keepWaiting = async () => {
    if (!jobRef.current) return;
    const { mode, extra } = lastGenRef.current;
    setTakingLong(false); setBusy(true);
    let timer;
    if (mode === "initial") timer = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 1400);
    try { const res = await pollJob(jobRef.current); handleGenResult(mode, extra, res, timer); }
    catch (e) {
      if (timer) clearInterval(timer);
      if (e.code === "timeout") { setTakingLong(true); setBusy(false); }
      else { toast.error("We couldn't create that concept. Try again."); if (mode === "initial") setStep(9); setBusy(false); }
    }
  };

  // "Try Again" — explicit fresh generation (new request_id → new job).
  const tryAgain = () => { setTakingLong(false); localStorage.removeItem(JOB_KEY); callGen(lastGenRef.current.mode, lastGenRef.current.extra); };


  const toggleSave = (c) => {
    setSaved((prev) => {
      const next = { ...prev, [c.id]: !prev[c.id] };
      if (next[c.id]) trackEvent("ai_concept_saved", undefined, { name: c.name });
      return next;
    });
  };
  const buildDesign = (c, artUrl) => ({
    dimensions: dims,
    track: { layout: c.layout || layout, lanes: c.lanes || lanes, markings: { type: "none" } },
    base_style: { category: "Solid", pattern: "solid", primary: "#111111" },
    border: { enabled: false },
    objects: [],
    mode: "ai",
    is_ai_design: true,
    ai_artwork: { url: artUrl, direction: c.direction, motif: MOTIF_KEY[motif], intensity, recipe: c.recipe, markings: c.markings || markings },
  });

  const use = async (c) => {
    if (busy) return;
    if (!c.inspiration_image) { toast.error("This concept has no image yet."); return; }
    setBusy(true);
    trackEvent("ai_concept_selected", undefined, { name: c.name, recipe: c.recipe, direction: c.direction, motif: c.motif });
    try {
      const art = await aiSelectConcept({ session_id: sessionRef.current, concept_id: c.id,
        direction: c.direction, motif: MOTIF_KEY[motif], intensity, recipe: c.recipe, dimensions: dims, layout, logo, markings });
      const url = `${process.env.REACT_APP_BACKEND_URL}${art.url}`;
      const design = buildDesign(c, url);
      design.ai_artwork.artwork_id = art.artwork_id;
      design.ai_artwork.concept_id = c.id;
      design.ai = { generatedByAI: true, designDirection: c.direction, motif: MOTIF_KEY[motif], intensity, layoutRecipe: c.recipe, markings };
      design.locked = true;
      trackEvent("ai_editor_opened");
      onUse(design);
    } catch (e) { toast.error("Could not open that design. Try again."); }
    setBusy(false);
  };

  const refinementsNote = remaining == null ? null : (remaining > 0 ? `${remaining} AI refinement${remaining === 1 ? "" : "s"} remaining` : "No AI refinements remaining");

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-2 sm:p-3" data-testid="ai-wizard">
      <div className="bg-pgt-surface border border-white/15 rounded-sm max-w-4xl w-full p-4 sm:p-6 max-h-[94vh] overflow-y-auto relative">
        <button onClick={onClose} data-testid="ai-close" className="absolute top-3 right-3 text-white/60 hover:text-white z-10"><X size={20} /></button>
        <div className="flex items-center gap-2 mb-4"><Sparkles size={18} className="text-pgt-green" /><h2 className="pgt-heading text-white text-2xl">AI Design My Turf</h2></div>

        {step === 1 && (
          <div className="space-y-4" data-testid="ai-step-size">
            <p className="text-white/70 text-sm">Your turf size (max {lim.max_length}m × {lim.max_width}m for instant order).</p>
            <div className="flex gap-4">
              {[["length", lim.max_length, lim.min_length], ["width", lim.max_width, lim.min_width]].map(([k, mx, mn]) => (
                <label key={k} className="flex-1 text-white/70 text-sm capitalize">{k} (m)
                  <input type="number" step="0.1" min={mn} max={mx} data-testid={`ai-${k}`} value={dims[k]}
                    onChange={(e) => setDims({ ...dims, [k]: e.target.value === "" ? mn : parseFloat(e.target.value) })}
                    className={`w-full mt-1 bg-pgt-bg border rounded-sm px-3 py-2 text-white ${dims[k] > mx + 1e-6 ? "border-pgt-green" : "border-white/15"}`} />
                </label>
              ))}
            </div>
            {overLimit ? (
              <div className="border border-pgt-green/40 bg-gradient-to-b from-pgt-green/10 to-transparent rounded-sm p-4" data-testid="ai-custom-quote">
                <div className="pgt-eyebrow mb-1">Larger Project?</div>
                <h3 className="font-display font-bold uppercase tracking-wide text-white text-lg mb-2">This Size Needs A Custom Quote</h3>
                <p className="text-sm text-white/60 leading-relaxed mb-3">We can make it — this size simply requires a custom quote. Our online designer supports turf up to {lim.max_width}m wide and {lim.max_length}m long. We can manufacture larger bespoke tracks — contact us and we'll create a bespoke quotation.</p>
                <a href={quoteUrl} target="_blank" rel="noreferrer" data-testid="ai-get-quote-btn" className="inline-block bg-pgt-green text-black font-display font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-sm hover:bg-pgt-greenHover transition-colors">Get A Custom Quote →</a>
              </div>
            ) : (
              <button data-testid="ai-next-1" onClick={() => setStep(2)} className="bg-pgt-green text-black font-display font-bold uppercase px-5 py-2.5 rounded-sm">Next</button>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4" data-testid="ai-step-layout">
            <p className="text-white/70 text-sm">How many lanes? (independent of turf width)</p>
            <div className="grid grid-cols-5 gap-2" data-testid="ai-lane-selector">
              {[1, 2, 3, 4, 5].map((n) => (
                <Btn key={n} on={lanes === n} onClick={() => { setLanes(n); if (n >= 2 && COMBINED_LINE_NUMBER.includes(markings.style)) mk({ style: "metre_markings" }); }} data-testid={`ai-lanes-${n}`}>{n}</Btn>
              ))}
            </div>
            <p className="text-white/40 text-[11px]">{lanes === 1 ? "Whole turf / single continuous lane." : `${lanes} equal lanes separated by ${lanes - 1} divider${lanes > 2 ? "s" : ""}.`}</p>
            <button data-testid="ai-next-2" onClick={() => setStep(3)} className="bg-pgt-green text-black font-display font-bold uppercase px-5 py-2.5 rounded-sm">Next</button>
          </div>
        )}

        {step === 3 && logoStage === "upload" && (
          <div className="space-y-4" data-testid="ai-step-logo">
            <p className="text-white/80 font-display uppercase tracking-wide">Do you want to upload a logo?</p>
            <p className="text-white/50 text-xs">Adding your logo lets the AI design the turf around your exact brand. No logo? Choose your own brand colours instead.</p>
            <label className="w-full flex items-center justify-center gap-2 border border-dashed border-white/25 rounded-sm py-8 text-white/70 hover:border-pgt-green cursor-pointer" data-testid="ai-upload">
              {busy ? <Loader2 className="animate-spin" /> : <Upload size={18} />} Yes — Upload PNG / JPG / SVG
              <input type="file" accept="image/*,.pdf" className="hidden" onChange={onLogo} />
            </label>
            <button data-testid="ai-no-logo" onClick={() => { setNoLogo(true); setBrandSel([]); setStep(31); }} className="w-full border border-white/15 text-white/70 font-display font-bold uppercase tracking-wide text-sm py-2.5 rounded-sm hover:border-pgt-green hover:text-pgt-green">No — I don't have a logo</button>
          </div>
        )}

        {step === 3 && logoStage === "confirm" && (
          <div className="space-y-4" data-testid="ai-step-logo-bg">
            <p className="text-white/80 font-display uppercase tracking-wide">We cleaned up your logo background</p>
            <p className="text-white/50 text-xs">We detected a flat background and removed it so your logo sits cleanly in the design — no white/black box. Choose which version to use.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-white/15 rounded-sm p-3 text-center">
                <div className="text-white/50 text-[11px] uppercase tracking-wide mb-2">Original</div>
                <div className="h-28 flex items-center justify-center bg-[#1a1a1a] rounded-sm"><img src={origB64} alt="original logo" className="max-h-full max-w-full object-contain" data-testid="ai-logo-original" /></div>
              </div>
              <div className="border border-pgt-green/40 rounded-sm p-3 text-center">
                <div className="text-pgt-green text-[11px] uppercase tracking-wide mb-2">Background Removed</div>
                <div className="h-28 flex items-center justify-center rounded-sm ai-checker"><img src={cleanB64} alt="cleaned logo" className="max-h-full max-w-full object-contain" data-testid="ai-logo-cleaned" /></div>
              </div>
            </div>
            <div className="flex gap-2">
              <button data-testid="ai-use-cleaned" onClick={() => confirmLogo(true)} className="flex-1 bg-pgt-green text-black font-display font-bold uppercase text-sm py-2.5 rounded-sm">Use Background Removed</button>
              <button data-testid="ai-keep-original" onClick={() => confirmLogo(false)} className="flex-1 border border-white/20 text-white/70 font-display font-bold uppercase text-sm py-2.5 rounded-sm hover:border-pgt-green">Keep Original</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4" data-testid="ai-step-brand">
            <p className="text-white/80 font-display uppercase tracking-wide">Confirm Your Brand Colours</p>
            <p className="text-white/50 text-xs">These become a hard colour lock — the AI will keep your design based on the colours you confirm (tap to include, up to 3). Mapped to real manufacturable turf colours.</p>
            <div className="flex gap-2 flex-wrap">{(brand.length ? brand : [{ hex: "#111111", name: "Black" }]).map((b, i) => {
              const on = brandSel.includes(b.hex); const rank = brandSel.indexOf(b.hex);
              return (
                <button key={i} data-testid={`ai-brand-${i}`} onClick={() => toggleBrand(b.hex)} className={`flex items-center gap-2 border rounded-sm px-2 py-1.5 transition-colors ${on ? "border-pgt-green bg-pgt-green/10" : "border-white/15 hover:border-white/40"}`}>
                  <span className="w-6 h-6 rounded-sm border border-white/20" style={{ background: b.hex }} />
                  <span className="text-white/80 text-xs">{b.name}</span>
                  {on && <span className="text-[9px] font-display uppercase text-pgt-green">{["Primary", "Secondary", "Accent"][rank] || "Use"}</span>}
                </button>
              );
            })}</div>
            <button data-testid="ai-next-4" disabled={!brandSel.length && brand.length > 0} onClick={() => setStep(5)} className="bg-pgt-green text-black font-display font-bold uppercase px-5 py-2.5 rounded-sm disabled:opacity-40">Choose a design direction</button>
          </div>
        )}

        {step === 31 && (
          <div className="space-y-4" data-testid="ai-step-colours">
            <p className="text-white/80 font-display uppercase tracking-wide">Choose Your Brand Colours</p>
            <p className="text-white/50 text-xs">Pick 2 or 3 colours — these become a HARD colour lock for your whole design (base, borders, accents, markings and branding). We won't default to green.</p>
            <div className="flex gap-2 flex-wrap">{turfCols.map((c, i) => {
              const on = brandSel.includes(c.hex); const rank = brandSel.indexOf(c.hex);
              return (
                <button key={i} data-testid={`ai-colour-${i}`} onClick={() => toggleBrand(c.hex)} className={`flex items-center gap-2 border rounded-sm px-2 py-1.5 transition-colors ${on ? "border-pgt-green bg-pgt-green/10" : "border-white/15 hover:border-white/40"}`}>
                  <span className="w-6 h-6 rounded-sm border border-white/20" style={{ background: c.hex }} />
                  <span className="text-white/80 text-xs">{c.name}</span>
                  {on && <span className="text-[9px] font-display uppercase text-pgt-green">{["Primary", "Secondary", "Accent"][rank] || "Use"}</span>}
                </button>
              );
            })}</div>
            <div className="flex items-center gap-2">
              <input value={hexInput} onChange={(e) => setHexInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addHex()} placeholder="Or add a hex e.g. #1A2B3C" data-testid="ai-hex-input" className="flex-1 bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white text-sm outline-none focus:border-pgt-green" />
              <button data-testid="ai-hex-add" onClick={addHex} className="border border-white/20 text-white/80 font-display font-bold uppercase text-xs px-4 py-2 rounded-sm hover:border-pgt-green">Add</button>
            </div>
            {brandSel.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="ai-colours-selected">
                {brandSel.map((hx, i) => (
                  <span key={hx} className="flex items-center gap-1.5 border border-pgt-green/40 bg-pgt-green/10 rounded-sm px-2 py-1 text-xs text-white/80">
                    <span className="w-4 h-4 rounded-sm border border-white/20" style={{ background: hx }} />{["Primary", "Secondary", "Accent"][i] || ""} {hx}
                    <button onClick={() => toggleBrand(hx)} className="text-white/50 hover:text-white ml-1"><X size={12} /></button>
                  </span>
                ))}
              </div>
            )}
            <button data-testid="ai-next-colours" disabled={brandSel.length < 2} onClick={() => setStep(32)} className="bg-pgt-green text-black font-display font-bold uppercase px-5 py-2.5 rounded-sm disabled:opacity-40">Next{brandSel.length < 2 ? " (pick at least 2)" : ""}</button>
          </div>
        )}

        {step === 32 && (
          <div className="space-y-4" data-testid="ai-step-branding">
            <p className="text-white/80 font-display uppercase tracking-wide">Do You Want Branding Added?</p>
            <p className="text-white/50 text-xs">Choose how your brand appears on the turf. Everything uses only your selected colours.</p>
            <div className="grid sm:grid-cols-3 gap-2">
              {[["none", "No Text Or Logo", "Clean colours-only design."], ["text", "Business Name Text", "Your gym name as a typographic wordmark."], ["ai_logo", "Create A Simple AI Logo", "AI makes a clean wordmark/emblem from your name."]].map(([k, t, d]) => (
                <button key={k} data-testid={`ai-branding-${k}`} onClick={() => setBrandingMode(k)} className={`text-left border rounded-sm p-3 transition-colors ${brandingMode === k ? "border-pgt-green" : "border-white/15 hover:border-pgt-green"}`}>
                  <div className="font-display font-bold uppercase text-white text-sm">{t}</div><div className="text-white/50 text-xs mt-1">{d}</div>
                </button>
              ))}
            </div>
            {(brandingMode === "text" || brandingMode === "ai_logo") && (
              <div className="space-y-2 border-t border-white/10 pt-3">
                <input value={bizName} onChange={(e) => setBizName(e.target.value.slice(0, 40))} placeholder="Business / gym name (required)" data-testid="ai-biz-name" className="w-full bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white text-sm outline-none focus:border-pgt-green" />
                <input value={slogan} onChange={(e) => setSlogan(e.target.value.slice(0, 60))} placeholder="Slogan / tagline (optional)" data-testid="ai-slogan" className="w-full bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white text-sm outline-none focus:border-pgt-green" />
                <input value={extraWording} onChange={(e) => setExtraWording(e.target.value.slice(0, 60))} placeholder="Extra wording (optional)" data-testid="ai-extra" className="w-full bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white text-sm outline-none focus:border-pgt-green" />
              </div>
            )}
            <button data-testid="ai-next-branding" disabled={brandingMode !== "none" && !bizName.trim()} onClick={() => setStep(5)} className="bg-pgt-green text-black font-display font-bold uppercase px-5 py-2.5 rounded-sm disabled:opacity-40">Choose A Design Direction</button>
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3" data-testid="ai-step-direction">
            <p className="text-white/70 text-sm">Choose your design direction</p>
            <div className="grid sm:grid-cols-2 gap-2">{DIRECTIONS.map(([k, t, d]) => (
              <button key={k} data-testid={`ai-dir-${k}`} onClick={() => { setDirection(k); trackEvent("ai_direction_selected", undefined, { direction: k }); setStep(6); }} className={`text-left border rounded-sm p-3 transition-colors ${direction === k ? "border-pgt-green" : "border-white/15 hover:border-pgt-green"}`}>
                <div className="font-display font-bold uppercase text-white text-sm">{t}</div><div className="text-white/50 text-xs mt-1">{d}</div>
              </button>
            ))}</div>
          </div>
        )}

        {step === 6 && (
          <div className="space-y-3" data-testid="ai-step-motif">
            <p className="text-white/70 text-sm">Choose your feature shape</p>
            <div className="flex flex-wrap gap-2">{MOTIFS.map((m) => <Btn key={m} on={motif === m} onClick={() => { setMotif(m); trackEvent("ai_motif_selected", undefined, { motif: MOTIF_KEY[m] }); }} data-testid={`ai-motif-${m.replace(/\s+/g, "-").toLowerCase()}`}>{m}</Btn>)}</div>
            <button data-testid="ai-next-6" onClick={() => setStep(7)} className="bg-pgt-green text-black font-display font-bold uppercase px-5 py-2.5 rounded-sm">Next</button>
          </div>
        )}

        {step === 7 && (
          <div className="space-y-3" data-testid="ai-step-intensity">
            <p className="text-white/70 text-sm">How bold?</p>
            <div className="flex gap-2">{INTENSITIES.map(([k, t]) => <Btn key={k} on={intensity === k} onClick={() => { setIntensity(k); trackEvent("ai_intensity_selected", undefined, { intensity: k }); }} data-testid={`ai-int-${k}`}>{t}</Btn>)}</div>
            <button data-testid="ai-next-7" onClick={() => setStep(8)} className="bg-pgt-green text-black font-display font-bold uppercase px-5 py-2.5 rounded-sm">Next</button>
          </div>
        )}

        {step === 8 && (
          <div className="space-y-4" data-testid="ai-step-markings">
            <p className="text-white/80 font-display uppercase tracking-wide">Choose Your Track Markings</p>
            <p className="text-white/50 text-xs">The AI paints these into the finished artwork, proportioned to your {dims.length}m × {dims.width}m turf.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {(layout === "two_lane" ? MARK_STYLES.filter(([k]) => !COMBINED_LINE_NUMBER.includes(k)) : MARK_STYLES).map(([k, t, d]) => (
                <button key={k} data-testid={`ai-mark-${k}`} onClick={() => mk({ style: k })} className={`text-left border rounded-sm p-3 transition-colors ${markings.style === k ? "border-pgt-green" : "border-white/15 hover:border-pgt-green"}`}>
                  <div className="font-display font-bold uppercase text-white text-sm">{t}</div><div className="text-white/50 text-xs mt-1">{d}</div>
                </button>
              ))}
            </div>
            {layout === "two_lane" && <p className="text-white/40 text-[11px]" data-testid="ai-two-lane-mark-note">On a 2‑lane track choose either metre lines or numbers — not both — so each narrow lane stays clean.</p>}
            {markings.style !== "none" && (
              <div className="space-y-3 border-t border-white/10 pt-3">
                <div><span className="text-xs uppercase tracking-wider text-white/60">Interval</span>
                  <div className="flex gap-2 mt-1">{[1, 2].map((iv) => <Btn key={iv} on={markings.interval === iv} onClick={() => mk({ interval: iv })} data-testid={`ai-mark-interval-${iv}`}>Every {iv}m</Btn>)}</div>
                </div>
                <div><span className="text-xs uppercase tracking-wider text-white/60">Marking position</span>
                  <div className="flex gap-2 mt-1 flex-wrap">{MARK_POS.map(([k, t]) => <Btn key={k} on={markings.position === k} onClick={() => mk({ position: k })} data-testid={`ai-mark-pos-${k}`}>{t}</Btn>)}</div>
                </div>
                {layout === "two_lane" && (
                  <div><span className="text-xs uppercase tracking-wider text-white/60">Two-lane markings</span>
                    <div className="flex gap-2 mt-1">{[["shared", "Shared"], ["per_lane", "Per Lane"]].map(([k, t]) => <Btn key={k} on={markings.lane_mode === k} onClick={() => mk({ lane_mode: k })} data-testid={`ai-mark-lane-${k}`}>{t}</Btn>)}</div>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Btn on={markings.start_line} onClick={() => mk({ start_line: !markings.start_line })} data-testid="ai-mark-start">Start Line</Btn>
                  <Btn on={markings.finish_line} onClick={() => mk({ finish_line: !markings.finish_line })} data-testid="ai-mark-finish">Finish Line</Btn>
                  <Btn on={markings.sf_text} onClick={() => mk({ sf_text: !markings.sf_text })} data-testid="ai-mark-sftext">START / FINISH Text</Btn>
                </div>
              </div>
            )}
            <button data-testid="ai-next-8" onClick={() => setStep(9)} className="bg-pgt-green text-black font-display font-bold uppercase px-5 py-2.5 rounded-sm">Next</button>
          </div>
        )}

        {step === 9 && (
          <div className="space-y-3" data-testid="ai-step-instructions">
            <p className="text-white/70 text-sm">Anything you'd like included? (optional)</p>
            <textarea data-testid="ai-instructions" value={instructions} onChange={(e) => setInstructions(e.target.value.slice(0, 160))} placeholder="e.g. Lots of arrows, keep it clean, include ZERO EXCUSES" className="w-full bg-pgt-bg border border-white/15 rounded-sm px-3 py-2 text-white h-20" />
            <button data-testid="ai-generate-btn" disabled={busy} onClick={() => callGen("initial")} className="bg-pgt-green text-black font-display font-bold uppercase px-6 py-3 rounded-sm disabled:opacity-50 inline-flex items-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Generate 3 Concepts
            </button>
          </div>
        )}

        {step === 10 && (
          <div className="py-16 flex flex-col items-center gap-4" data-testid="ai-loading">
            <Loader2 size={34} className="animate-spin text-pgt-green" />
            <div className="text-white/80 font-display uppercase tracking-wide">{STAGES[stage]}</div>
            {takingLong && (
              <div className="mt-4 max-w-md text-center space-y-4" data-testid="ai-taking-long">
                <p className="text-white/70 text-sm leading-relaxed">{LONG_MSG}</p>
                <div className="flex items-center justify-center gap-2">
                  <button data-testid="ai-keep-waiting" disabled={busy} onClick={keepWaiting} className="bg-pgt-green text-black font-display font-bold uppercase tracking-wide text-sm px-5 py-2.5 rounded-sm disabled:opacity-40">Keep Waiting</button>
                  <button data-testid="ai-try-again" disabled={busy} onClick={tryAgain} className="border border-white/20 text-white/70 font-display font-bold uppercase tracking-wide text-sm px-5 py-2.5 rounded-sm hover:border-pgt-green disabled:opacity-40">Try Again</button>
                </div>
              </div>
            )}
          </div>
        )}

        {step === 11 && concepts && (() => {
          const idx = Math.min(viewIdx, concepts.length - 1);
          const c = concepts[idx];
          return (
          <div className="space-y-4" data-testid="ai-concepts">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-white/70 text-sm">Concept {idx + 1} of {concepts.length} — inspect the whole design, then choose your favourite.</p>
              {refinementsNote && <span data-testid="ai-refinements-remaining" className="text-xs font-display uppercase tracking-wide text-pgt-green/90 bg-pgt-green/10 border border-pgt-green/30 rounded-sm px-2 py-1">{refinementsNote}</span>}
            </div>

            <div className="relative bg-[#0d0d0d] rounded-sm border border-white/10 flex items-center justify-center p-3" data-testid="ai-concept-viewer">
              {concepts.length > 1 && (
                <button data-testid="ai-concept-prev" onClick={() => setViewIdx((idx - 1 + concepts.length) % concepts.length)} className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-pgt-green hover:text-black transition-colors"><ChevronLeft size={20} /></button>
              )}
              {c.inspiration_image ? (
                <img src={imgUrl(c.inspiration_image)} alt={c.name} data-testid={`ai-concept-${idx}`} className="w-full h-auto object-contain rounded-sm" style={{ maxHeight: "58vh" }} />
              ) : (
                <div className="w-full flex items-center justify-center text-white/30 text-[11px] uppercase" style={{ aspectRatio: `${dims.length} / ${dims.width}`, minHeight: 120 }}>AI concept unavailable</div>
              )}
              {concepts.length > 1 && (
                <button data-testid="ai-concept-next" onClick={() => setViewIdx((idx + 1) % concepts.length)} className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-9 h-9 flex items-center justify-center rounded-full bg-black/60 text-white hover:bg-pgt-green hover:text-black transition-colors"><ChevronRight size={20} /></button>
              )}
              <button data-testid={`ai-save-${idx}`} onClick={() => toggleSave(c)} title="Favourite" className={`absolute top-2 right-2 z-10 p-1.5 rounded-sm bg-black/50 ${saved[c.id] ? "text-pgt-green" : "text-white/70 hover:text-white"}`}><Heart size={16} fill={saved[c.id] ? "#00D26A" : "none"} /></button>
            </div>

            {concepts.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1" data-testid="ai-concept-thumbs">
                {concepts.map((t, i) => (
                  <button key={t.id} data-testid={`ai-concept-thumb-${i}`} onClick={() => setViewIdx(i)} className={`flex-shrink-0 rounded-sm overflow-hidden border-2 transition-colors ${i === idx ? "border-pgt-green" : "border-white/15 hover:border-white/40"}`} style={{ width: 96, height: 40, background: "#111" }}>
                    {t.inspiration_image ? <img src={imgUrl(t.inspiration_image)} alt={t.name} className="w-full h-full object-contain" /> : <span className="text-white/30 text-[9px]">—</span>}
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-display font-bold uppercase text-white text-lg">{c.name}</div>
                <div className="text-white/50 text-xs">{c.blurb}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`ai-more-like-${idx}`} disabled={busy || limitReached} onClick={() => callGen("more_like_this", { base_recipe: c.recipe })} className="inline-flex items-center gap-1 border border-white/15 text-white/70 text-xs font-display uppercase px-3 py-2 rounded-sm hover:border-pgt-green disabled:opacity-40"><Wand2 size={14} /> More Like This</button>
                <button data-testid={`ai-regen-${idx}`} disabled={busy || limitReached} onClick={() => callGen("regenerate_one", { _slotId: c.id })} className="inline-flex items-center gap-1 border border-white/15 text-white/70 text-xs font-display uppercase px-3 py-2 rounded-sm hover:border-pgt-green disabled:opacity-40" title="Regenerate this one"><RefreshCw size={14} /></button>
              </div>
            </div>

            <button data-testid={`ai-use-${idx}`} disabled={busy} onClick={() => use(c)} className="w-full bg-pgt-green text-black font-display font-bold uppercase text-base py-3 rounded-sm disabled:opacity-50">Use This Design</button>
            <p className="text-[11px] text-white/40 text-center">This exact image opens as your full-size AI Artwork Proof — no second canvas, no manual editor.</p>

            {limitReached ? (
              <div data-testid="ai-limit-message" className="border border-white/15 bg-white/5 rounded-sm p-3 text-white/70 text-sm">{LIMIT_MSG}</div>
            ) : (
              <button data-testid="ai-generate-more" disabled={busy} onClick={() => callGen("more")} className="inline-flex items-center gap-2 border border-pgt-green/50 text-pgt-green font-display font-bold uppercase text-sm px-4 py-2 rounded-sm hover:bg-pgt-green/10 disabled:opacity-40">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Copy size={14} />} Generate 3 More
              </button>
            )}
          </div>
          );
        })()}
      </div>
    </div>
  );
}
